import { compose, filter, get, keyBy, identity, isNull, pick, uniq, at } from 'lodash/fp';
import { createSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { array, nonEmptyArray, number, ord, record } from 'fp-ts';
import { createReducer, createAsyncMetaSelectors, createAction } from '@mews/framework';
import { getNullableOrd, getOrFalse, getOrNull, getStringArrayProp, getStringProp } from '@mews/fp';
import { CommanderThunkAction, ApplicationState } from '@mews-commander/types';
import { EntityDeletionState, GetRateParameters, Rate, RateGroup, RateType } from '@mews-commander/data-access';
import { Option } from '@mews-commander/legacy-data-structures';
import { createFetchAction, mapId, notEmpty, sortByName, createOptionsSelector } from '@mews-commander/utils';
import moduleSelector from '../selector';
import { META_STATE_FAILED, META_STATE_STARTED, META_STATE_SUCCEEDED } from '../constants';
import {
    RATES_FETCH,
    RATES_FETCH_FAILED,
    RATES_FETCH_STARTED,
    RATES_FETCH_SUCCEEDED,
    RATES_DELETE_STARTED,
    RATES_DELETE_SUCCEEDED,
    RATES_DELETE_FAILED,
} from './actionTypes';
import { getEnterpriseIdFromServiceId, setServices } from './services';
import { getRateGroups, rateGroupOrd, setRateGroups } from './rateGroups';
import { Entities } from './utils';

const rateOrd = ord.contramap((rate: Rate) => rate.ordering)(number.Ord);
const getRateByRateGroupOrd = (rateGroups: Entities<RateGroup>) =>
    ord.contramap((rate: Rate) => rateGroups[rate.rateGroupId])(getNullableOrd(rateGroupOrd));

const sortRates = (rateGroups: Entities<RateGroup>) => array.sortBy([getRateByRateGroupOrd(rateGroups), rateOrd]);
export const NAME = 'rates';

const fetch =
    (params: GetRateParameters): CommanderThunkAction =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.rates.getAll(params).then(({ rates, rateGroups, services }: $TSFixMe) => {
            if (rates && notEmpty(rates)) {
                // @ts-expect-error $TSFixMeError: Expected 2 arguments, but got 1.
                dispatch(setRates(rates));
            }
            if (rateGroups && notEmpty(rateGroups)) {
                // @ts-expect-error $TSFixMeError: Expected 2 arguments, but got 1.
                dispatch(setRateGroups(rateGroups));
            }
            if (services && notEmpty(services)) {
                dispatch(setServices(services));
            }

            return { rates, rateGroups, services };
        });

        void dispatch(createFetchAction(RATES_FETCH, promise));

        return promise;
    };

export const fetchRates =
    ({ serviceIds, rateIds, serviceDeletionState = EntityDeletionState.NonDeleted }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const enterpriseIds: string[] = uniq(
            serviceIds.map((serviceId: $TSFixMe) => getEnterpriseIdFromServiceId(getState(), { serviceId }))
        );

        return dispatch(fetch({ serviceIds, rateIds, enterpriseIds, serviceDeletionState }));
    };

export const setRates = (rates: $TSFixMe, scope: $TSFixMe) =>
    createAction(RATES_FETCH_SUCCEEDED, { rates, serviceId: scope });

export const ratesReducer = createReducer(
    { data: {}, ...META_STATE_SUCCEEDED },
    {
        [RATES_FETCH_STARTED]: ({ data }: $TSFixMe) => ({
            data,
            ...META_STATE_STARTED,
        }),
        [RATES_FETCH_SUCCEEDED]: ({ data }: $TSFixMe, { rates }: $TSFixMe) => ({
            data: {
                ...data,
                ...keyBy('id', rates),
            },
            ...META_STATE_SUCCEEDED,
        }),
        [RATES_FETCH_FAILED]: ({ data }: $TSFixMe) => ({
            data,
            ...META_STATE_FAILED,
        }),

        [RATES_DELETE_STARTED]: ({ data }: $TSFixMe) => ({
            data,
            ...META_STATE_STARTED,
        }),
        [RATES_DELETE_SUCCEEDED]: ({ data }: { data: Record<Rate['id'], Rate> }, deletedRateIds: string[]) => ({
            data: pipe(
                data,
                record.filter(({ id }) => !deletedRateIds.includes(id))
            ),
            ...META_STATE_SUCCEEDED,
        }),
        [RATES_DELETE_FAILED]: ({ data }: $TSFixMe) => ({
            data,
            ...META_STATE_FAILED,
        }),
    }
);
const getRatesEntities = createSelector(moduleSelector, (module) => module[NAME] || {});

const getRatesMeta = createSelector(getRatesEntities, get('meta'));
export const [areRatesFetching] = createAsyncMetaSelectors(getRatesMeta);

export const getRates = createSelector<ApplicationState, unknown, Entities<Rate>>(getRatesEntities, get('data'));

export const getRate = createSelector(getRates, getStringProp('rateId'), (rates, rateId) => rates[rateId] || {});

export const getRateName = createSelector(getRate, get('name'));

export const getRateNameVariants = createSelector(getRate, pick(['name', 'shortName']));

export const getRateRateGroupId = createSelector(getRate, get('rateGroupId'));

export const getRateEnabled = createSelector(getRate, getOrFalse('isEnabled'));

export const getRateBusinessSegmentId = createSelector(getRate, getOrNull('businessSegmentId'));

// @ts-expect-error $TSFixMeError: 'getRateCurrency' implicitly has type 'any' because it does not have a type annotation and is referenced directly or ind...
export const getRateCurrency = createSelector(identity, getRate, (state, { baseRateId, directBasePrice }) =>
    isNull(directBasePrice) ? getRateCurrency(state, { rateId: baseRateId }) : get('currency', directBasePrice)
);

export const getServiceRates = createSelector(getStringProp('serviceId'), getRates, (serviceId, rates) =>
    filter({ serviceId })(rates)
);

export const getBookableServiceRates = createSelector(
    getServiceRates,
    // @ts-expect-error $TSFixMeError: Property 'isEnabled' does not exist on type '{ serviceId: { toString: ...; charAt: ...; charCodeAt: ...; concat: ...; in...
    filter(({ isEnabled, type }) => isEnabled && type !== RateType.AvailabilityBlock)
);

export const getServiceRateOptions = createOptionsSelector(getServiceRates, { sortFunction: sortByName });

export const getRateGroupRatesOptions = createSelector(
    // @ts-expect-error
    getServiceRates,
    getRateGroups,
    (rates: Array<Rate>, rateGroups: Entities<RateGroup>) =>
        pipe(
            rates,
            array.filter(({ isDeleted }) => !isDeleted),
            nonEmptyArray.groupBy((rate: Rate) => rate.rateGroupId),
            Object.entries<Rate[]>,
            array.map(([rateGroupId, rateGroupRates]) => {
                const rateGroupOptions = pipe(
                    rateGroupRates,
                    array.map((rate: Rate) => Option({ value: rate.id, label: rate.name }))
                );
                return {
                    groupLabel: rateGroups[rateGroupId].name,
                    groupOptions: rateGroupOptions,
                };
            })
        )
);

export const getBookableServiceRateOptions = createOptionsSelector(getBookableServiceRates, {
    sortFunction: sortByName,
});

export const getServiceRateIds = createSelector(getServiceRates, mapId);

export const hasServiceRates = compose(notEmpty, getServiceRates);

export const getRatesByIds = createSelector(getStringArrayProp('ids'), getRates, (ids, rates) => at(ids, rates));

export const getSortedRatesByIds = createSelector(getRatesByIds, getRateGroups, (rates, rateGroups) =>
    sortRates(rateGroups)(rates)
);
